import { DependencyList, EffectCallback, useEffect, useLayoutEffect, useRef } from 'react';

/**
 * A dupe of React's Destructor type.
 * TODO(jordanhurt) - It would be ideal to rely solely on use `ReturnType<EffectCallback>`,
 * but this produces a bizarre error. Context:
 * https://github.com/cowpaths/mn/pull/69216/files#r1388238042
 */
type Destructor = () => void;
type DestructorOrVoid = void | Destructor;

/**
 * Similar to a regular EffectCallback, but can return a promise that
 * resolves a cleanup function instead of returning it synchronously.
 * When a promise is returned, the effect will wait for it to resolve
 * and invoke the cleanup function before running the next effect.
 *
 * The callback also receives an `isActive` function that can be used
 * to check if the effect is still active. This can be used to cancel
 * asynchronous work.
 */
export type AsyncEffectCallback = (
  isActive: () => boolean,
) =>
  | DestructorOrVoid
  | ReturnType<EffectCallback>
  | PromiseLike<DestructorOrVoid>
  | PromiseLike<ReturnType<EffectCallback>>;

/**
 * Conveniently wraps useEffect with support promise-based callbacks.
 * Receives an isActive function that can be used to interrupt and cancel
 * the asynchronous work.
 *
 * @param callback A callback that can return a cleanup function or a promise that resolves one
 * @param deps An array of dependencies that will trigger the effect when changed
 * @examples
 * ```ts
 *  // Synchronous Cleanup:
 * // A cleanup function is returned synchronously to cancel fetch immediately.
 * // It is safe for the next fetch to begin before the current one is canceled.
 * function useFetch<T>(url: string): Promise<T> {
 *   const [result, setResult] = useState<FetchResult<T>>({ loading: false });
 *
 *   useAsyncEffect(isActive => {
 *     if (!isActive()) return;
 *
 *     setResult({ loading: true });
 *
 *     const abortController = new AbortController();
 *
 *     fetch(url, { signal: abortController.signal })
 *       .then(result => {
 *         isActive() && setResult({ data: result, loading: false });
 *       })
 *       .catch(error => {
 *         isActive() && setResult({ error, loading: false });
 *       });
 *
 *     // Note that the cleanup function is returned synchronously
 *     // so that cleanup is immediate if the effect is interrupted.
 *     return () => abortController.abort();
 *   }, [url]);

 *   return data;
 * }
 *
 *  // Async Cleanup:
 *  // A cleanup function is resolved from a promise, forcing the
 *  // next callback to wait until the previous one has resolved.
 * function useClient(url: string): Client {
 *   const [client, setClient] = useState<Client>();
 *
 *   useAsyncEffect(async isActive => {
 *     if (!isActive()) return;
 *
 *     const client = new Client(url);
 *     await client.init();
 *
 *     isActive() && setClient(client);
 *
 *     // Note that the next client cannot be created
 *     // until the previous one has been destroyed because
 *     // the cleanup function is resolved from a promise.
 *     return () => client.destroy();
 *   }, [url]);
 * }
 * ```
 */
export function useAsyncEffect(callback: AsyncEffectCallback, deps?: DependencyList) {
  useAsyncEffectInternal(callback, deps, useEffect);
}

/**
 * Conveniently wraps useLayoutEffect with support promise-based callbacks.
 * Receives an isActive function that can be used to interrupt and cancel
 * the asynchronous work.
 *
 * @param callback A callback that can return a cleanup function or a promise that resolves one
 * @param deps An array of dependencies that will trigger the effect when changed
 * @examples
 * ```ts
 *  // Synchronous Cleanup:
 * // A cleanup function is returned synchronously to cancel fetch immediately.
 * // It is safe for the next fetch to begin before the current one is canceled.
 * function useFetch<T>(url: string): Promise<T> {
 *   const [result, setResult] = useState<FetchResult<T>>({ loading: false });
 *
 *   useAsyncLayoutEffect(isActive => {
 *     if (!isActive()) return;
 *
 *     setResult({ loading: true });
 *
 *     const abortController = new AbortController();
 *
 *     fetch(url, { signal: abortController.signal })
 *       .then(result => {
 *         isActive() && setResult({ data: result, loading: false });
 *       })
 *       .catch(error => {
 *         isActive() && setResult({ error, loading: false });
 *       });
 *
 *     // Note that the cleanup function is returned synchronously
 *     // so that cleanup is immediate if the effect is interrupted.
 *     return () => abortController.abort();
 *   }, [url]);

 *   return data;
 * }
 *
 *  // Async Cleanup:
 *  // A cleanup function is resolved from a promise, forcing the
 *  // next callback to wait until the previous one has resolved.
 * function useClient(url: string): Client {
 *   const [client, setClient] = useState<Client>();
 *
 *   useAsyncLayoutEffect(async isActive => {
 *     if (!isActive()) return;
 *
 *     const client = new Client(url);
 *     await client.init();
 *
 *     isActive() && setClient(client);
 *
 *     // Note that the next client cannot be created
 *     // until the previous one has been destroyed because
 *     // the cleanup function is resolved from a promise.
 *     return () => client.destroy();
 *   }, [url]);
 * }
 * ```
 */
export function useAsyncLayoutEffect(callback: AsyncEffectCallback, deps?: DependencyList) {
  useAsyncEffectInternal(callback, deps, useLayoutEffect);
}

/**
 * useAsyncEffect is a convenience function for defining effect hooks
 * with promise-based callbacks.
 *
 * Because the callback passed to useEffect must be synchronous and
 * cancellable, it can be awkward to use promise-based effects,
 * especially with the async-await syntax.
 */
function useAsyncEffectInternal<T>(
  callback: AsyncEffectCallback,
  deps?: DependencyList,
  useEffectImpl: typeof useEffect = useEffect,
): void {
  const cleanupRef = useRef<ReturnType<EffectCallback> | PromiseLike<ReturnType<EffectCallback>>>();

  useEffectImpl(() => {
    let active = true;
    const isActive = () => active;

    // When specified, don't run the next callback until the previous one has finished
    if (isPromiseLike(cleanupRef.current)) {
      // Chain our callback to the previous ones
      const chain = cleanupRef.current;
      const getResult = () => (isActive() ? callback(isActive) : () => {});

      cleanupRef.current = chain.then(getResult);
    } else {
      // Just overwrite the previous callback
      // NOTE: We're just reusing the ref for convenience here. It isn't necessary.
      cleanupRef.current = callback(isActive);
    }

    // Capture a snapshot of our cleanup function so it can't be overwritten
    const cleanup = cleanupRef.current;

    // Return a wrapper around it that will manage cancellation and invoke the destructor
    return () => {
      active = false;

      if (isPromiseLike(cleanup)) {
        cleanup?.then(destructor => destructor?.());
      } else {
        cleanup?.();
      }
    };
    // NOTE: In this rare case, ignoring exhaustive deps is correct.
    // This effect should _only_ run in response to one of its provided
    // deps changing. We also don't need to worry about the `callback`
    // reference changing because `useEffect` expects a stable
    // `EffectCallback` as its first parameter.
  }, deps);
}

function isPromiseLike(value: any): value is PromiseLike<any> {
  return value && typeof value.then === 'function';
}
